import { SkillLevelBar } from '@/components/learning/skill-level-bar'
import { SkillRadarChart } from '@/components/learning/skill-radar-chart'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  useOverallProgress,
  useRefreshLearningContext,
  useSkillProfile,
  useTopics,
  useWeeklyProgress,
} from '@/hooks/use-learning'
import type { LearningTopicWithProgress, SkillLevel } from '@/types'
import {
  ArrowLeft,
  BookOpen,
  Clock,
  Loader2,
  RefreshCw,
  Sparkles,
  Target,
  TrendingUp,
} from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'

function formatTime(minutes: number): string {
  if (minutes < 60) return `${minutes}m`
  const totalHours = Math.floor(minutes / 60)
  const mins = minutes % 60
  if (totalHours < 24) {
    return mins > 0 ? `${totalHours}h ${mins}m` : `${totalHours}h`
  }
  const days = Math.floor(totalHours / 24)
  const hours = totalHours % 24
  const parts = [`${days}d`]
  if (hours > 0) parts.push(`${hours}h`)
  if (mins > 0) parts.push(`${mins}m`)
  return parts.join(' ')
}

function SummaryBanner({
  completedTopics,
  enrolledTopics,
  totalMinutes,
  skillCount,
  weeklyPercent,
}: {
  completedTopics: number
  enrolledTopics: number
  totalMinutes: number
  skillCount: number
  weeklyPercent: number
}) {
  const stats = [
    {
      label: 'Topics Completed',
      value: `${completedTopics} / ${enrolledTopics}`,
      icon: BookOpen,
    },
    {
      label: 'Time Invested',
      value: formatTime(totalMinutes),
      icon: Clock,
    },
    {
      label: 'Skills Developed',
      value: String(skillCount),
      icon: TrendingUp,
    },
    {
      label: 'Weekly Goal',
      value: `${weeklyPercent}%`,
      icon: Target,
    },
  ]

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
      {stats.map((stat) => (
        <Card key={stat.label}>
          <CardContent className="py-4 px-4">
            <div className="flex items-center gap-2 mb-1">
              <stat.icon className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">{stat.label}</span>
            </div>
            <p className="text-2xl font-bold">{stat.value}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

function SkillBreakdown({
  currentSkills,
  targetSkills,
  topics,
}: {
  currentSkills: Record<string, SkillLevel>
  targetSkills?: Record<string, SkillLevel> | null | undefined
  topics: LearningTopicWithProgress[]
}) {
  const skills = Object.entries(currentSkills)

  if (skills.length === 0) return null

  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Skill Breakdown</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {skills.map(([name, level]) => {
            const targetLevel = targetSkills?.[name]
            const matchingTopic = topics.find((t) =>
              t.title.toLowerCase().includes(name.toLowerCase()),
            )

            return (
              <div key={name} className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium capitalize mb-1">{name}</p>
                  <SkillLevelBar currentLevel={level} targetLevel={targetLevel} />
                </div>
                {matchingTopic && (
                  <Link
                    to={`/learn/${matchingTopic.id}`}
                    className="text-xs text-primary hover:underline whitespace-nowrap mt-1"
                  >
                    View topic
                  </Link>
                )}
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}

function TopicProgressList({
  topics,
}: {
  topics: Array<{
    topic_id: number
    topic_title: string
    total_modules: number
    completed_modules: number
    progress_percent: number
    total_time_spent_minutes: number
  }>
}) {
  const navigate = useNavigate()

  if (topics.length === 0) return null

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Topic Progress</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {topics.map((topic) => (
            <button
              type="button"
              key={topic.topic_id}
              className="w-full text-left"
              onClick={() => navigate(`/learn/${topic.topic_id}`)}
            >
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-medium truncate pr-2">{topic.topic_title}</p>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {topic.completed_modules}/{topic.total_modules} modules
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all"
                  style={{ width: `${Math.min(topic.progress_percent, 100)}%` }}
                />
              </div>
              {topic.total_time_spent_minutes > 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  {formatTime(topic.total_time_spent_minutes)} spent
                </p>
              )}
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

export function LearnStatsPage() {
  const { data: profile, isLoading: profileLoading } = useSkillProfile()
  const { data: overallProgress, isLoading: progressLoading } = useOverallProgress()
  const { data: weeklyProgress } = useWeeklyProgress()
  const { data: topicsData } = useTopics(1, 50)
  const refreshContext = useRefreshLearningContext()

  const isLoading = profileLoading || progressLoading
  const topics = topicsData?.items || []
  const currentSkills = profile?.current_skills || {}
  const targetSkills = profile?.target_skills ?? null
  const skillCount = Object.keys(currentSkills).length

  if (isLoading) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="w-full px-6 py-8">
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </div>
    )
  }

  // No profile - prompt onboarding
  if (!profile?.learning_goal) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="w-full px-6 py-8">
          <div className="mb-6">
            <Link to="/learn">
              <Button variant="ghost" size="sm" className="gap-1">
                <ArrowLeft className="h-4 w-4" />
                Back to Learn
              </Button>
            </Link>
          </div>
          <Card>
            <CardContent className="py-12 text-center">
              <Sparkles className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="font-semibold mb-2">No learning profile yet</h3>
              <p className="text-muted-foreground mb-4">
                Complete onboarding to track your skill progress and see stats.
              </p>
              <Link to="/learn/onboard">
                <Button>Get Started</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="w-full px-6 py-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Link to="/learn" className="shrink-0">
              <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-9 sm:w-auto sm:px-3">
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden sm:inline ml-1">Back</span>
              </Button>
            </Link>
            <h1 className="text-xl sm:text-2xl font-bold truncate">Learning Stats</h1>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={() => refreshContext.mutate()}
            disabled={refreshContext.isPending}
          >
            {refreshContext.isPending ? (
              <>
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                <span className="hidden sm:inline">Refreshing...</span>
                <span className="sm:hidden">Refresh</span>
              </>
            ) : (
              <>
                <RefreshCw className="mr-1.5 h-4 w-4" />
                <span className="hidden sm:inline">Refresh AI Context</span>
                <span className="sm:hidden">Refresh</span>
              </>
            )}
          </Button>
        </div>

        {/* Summary Banner */}
        <SummaryBanner
          completedTopics={overallProgress?.completed_topics ?? 0}
          enrolledTopics={overallProgress?.enrolled_topics ?? 0}
          totalMinutes={overallProgress?.total_time_spent_minutes ?? 0}
          skillCount={skillCount}
          weeklyPercent={weeklyProgress?.percent ?? 0}
        />

        {/* AI Learning Context */}
        {profile?.user_learning_context &&
          (() => {
            const ctx = profile.user_learning_context
            // biome-ignore lint/complexity/useLiteralKeys: TypeScript index signature
            const careerSummary = ctx['career_summary']
            // biome-ignore lint/complexity/useLiteralKeys: TypeScript index signature
            const recommendedFocus = ctx['recommended_focus']
            // biome-ignore lint/complexity/useLiteralKeys: TypeScript index signature
            const keyStrengths = ctx['key_strengths']
            // biome-ignore lint/complexity/useLiteralKeys: TypeScript index signature
            const developmentAreas = ctx['development_areas']
            return (
              <Card className="mb-6">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">AI Learning Context</CardTitle>
                    {profile.context_refreshed_at && (
                      <span className="text-xs text-muted-foreground">
                        Updated {new Date(profile.context_refreshed_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 text-sm">
                    {typeof careerSummary === 'string' && (
                      <div>
                        <p className="font-medium text-muted-foreground mb-0.5">Career Summary</p>
                        <p>{careerSummary}</p>
                      </div>
                    )}
                    {typeof recommendedFocus === 'string' && (
                      <div>
                        <p className="font-medium text-muted-foreground mb-0.5">
                          Recommended Focus
                        </p>
                        <p>{recommendedFocus}</p>
                      </div>
                    )}
                    {Array.isArray(keyStrengths) && keyStrengths.length > 0 && (
                      <div>
                        <p className="font-medium text-muted-foreground mb-0.5">Key Strengths</p>
                        <div className="flex flex-wrap gap-1">
                          {(keyStrengths as string[]).map((strength) => (
                            <span
                              key={strength}
                              className="inline-flex items-center rounded-md bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 border border-green-200"
                            >
                              {strength}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {Array.isArray(developmentAreas) && developmentAreas.length > 0 && (
                      <div>
                        <p className="font-medium text-muted-foreground mb-0.5">
                          Development Areas
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {(developmentAreas as string[]).map((area) => (
                            <span
                              key={area}
                              className="inline-flex items-center rounded-md bg-yellow-50 px-2 py-0.5 text-xs font-medium text-yellow-700 border border-yellow-200"
                            >
                              {area}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )
          })()}

        {/* Radar Chart */}
        {skillCount > 0 && (
          <Card className="mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Skill Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <SkillRadarChart currentSkills={currentSkills} targetSkills={targetSkills} />
            </CardContent>
          </Card>
        )}

        {/* Per-Skill Breakdown */}
        <SkillBreakdown currentSkills={currentSkills} targetSkills={targetSkills} topics={topics} />

        {/* Topic Progress */}
        <TopicProgressList topics={overallProgress?.topics ?? []} />
      </div>
    </div>
  )
}
